import { computed, ref, type Ref } from 'vue'
// Remove vehiclesStore import if no longer needed directly for base options/counts
// import { useVehiclesStore } from '~/stores/vehicles' 
import { useFrontPageStore, type FrontPageFilters } from '~/stores/frontPage'
import type { 
  FilterOption, // FilterOption should include count now
  Vehicle, 
  ValueDisplayPair 
} from '~/types'

// --- Remove Local Types if no longer needed ---
// interface AvailableFilters { ... }
// interface LocalValueDisplayPair { ... }

// --- Modify FilterOption type definition if needed elsewhere to include count ---
// Ensure FilterOption in ~/types includes an optional `count?: number` 

// --- REMOVE unused helpers ---
// function getModelMakeDisplay(...) { ... }

export function useFilteredOptions(
  // No arguments needed
) {
  // Remove vehiclesStore if not used
  // const vehiclesStore = useVehiclesStore()
  const frontPageStore = useFrontPageStore()
  const modelSearchQuery = ref('')

  // --- REMOVE getBaseOptions --- 
  // const getBaseOptions = (...) => { ... }

  // --- REMOVE vehicleMatchesOtherFilters --- 
  // const vehicleMatchesOtherFilters = (...) => { ... }

  // --- Update Computed Properties to use frontPageStore dynamic getters --- 

  // Filtered conditions
  const filteredConditions = computed(() => {
    // Directly return the dynamic options from the store
    return frontPageStore.dynamicConditions;
  });

  // Filtered makes
  const filteredMakes = computed(() => {
    // Directly return the dynamic options from the store
    return frontPageStore.dynamicMakes;
  });

  // Filtered models with search applied
  const filteredModels = computed(() => {
    let models = frontPageStore.dynamicModels; // Get dynamic models (already filtered by other active filters)

    // Apply local search query on top
    if (modelSearchQuery.value) {
      const searchTerm = modelSearchQuery.value.toLowerCase()
      models = models.filter(model => 
        model.displayValue.toLowerCase().includes(searchTerm) ||
        (model.displayMake?.toLowerCase().includes(searchTerm)) // Search make display name too
      );
    }
    
    // Options from store already include displayMake and count
    return models;
  });

  // Group models by make (using options from the store which include displayMake)
  const groupedModels = computed(() => {
    const models = filteredModels.value; // Use the locally search-filtered models
    if (!models || models.length === 0) return {};
    
    const grouped: Record<string, FilterOption[]> = {};
    models.forEach(model => {
      const make = model.displayMake || 'Other'; 
      if (!grouped[make]) { grouped[make] = []; }
      grouped[make].push(model);
    });

    // Sort makes alphabetically, then models within makes
    return Object.keys(grouped)
      .sort()
      .reduce((obj: Record<string, FilterOption[]>, make) => {
        if (grouped[make]) {
          obj[make] = grouped[make].sort((a, b) =>
            a.displayValue.localeCompare(b.displayValue)
          );
        }
        return obj;
      }, {});
  });

  // Filtered body types
  const filteredBodyTypes = computed(() => {
    // Directly return the dynamic options from the store
    return frontPageStore.dynamicBodyTypes;
  });

  // --- Remove other filtered options if FacetedSearchWidget doesn't use them ---
  // const filteredTransmissions = computed(() => { ... });
  // const filteredDrivetrains = computed(() => { ... });
  // const filteredFuelTypes = computed(() => { ... });

  // Clear model search
  const clearModelSearch = () => {
    modelSearchQuery.value = ''
  }
  
  // Update isFilterOptionEnabled to check the count from store options
  const isFilterOptionEnabled = (filterName: keyof FrontPageFilters, optionValue: string): boolean => {
      let options: FilterOption[] = [];
      switch (filterName) {
          case 'condition': options = frontPageStore.dynamicConditions; break;
          case 'make': options = frontPageStore.dynamicMakes; break;
          case 'model': options = frontPageStore.dynamicModels; break; // Check against base dynamic models before search
          case 'body': options = frontPageStore.dynamicBodyTypes; break;
          // Add cases for transmission, drivetrain, fuel if kept
          default: return false;
      }
      const option = options.find(opt => opt.value === optionValue);
      return (option?.count ?? 0) > 0; // Enabled if found and count > 0
  };

  // --- REMOVE locallyFilteredVehicles --- 
  // const locallyFilteredVehicles = computed(() => { ... });

  return {
    modelSearchQuery,
    filteredConditions,
    filteredMakes,
    filteredModels, // This now includes search filtering
    groupedModels,
    filteredBodyTypes,
    // Remove transmission, drivetrain, fuel if not used by component
    // filteredTransmissions,
    // filteredDrivetrains,
    // filteredFuelTypes,
    clearModelSearch,
    isFilterOptionEnabled
  }
} 